/**
 * BoardEditPopover — the board's one small anchored dialog for W2 structural
 * ops (rename / add module / add interface / edge process chooser). Pure
 * view: state + commits live in useBoardEditing. Rendered by BoardPane in
 * its own chrome layer (no board-internal DOM), centered near the top of the
 * pane — position-precise anchoring at the node is later polish.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { Button, Tooltip } from "@archlang/ui";
import type { BoardEditAction, BoardEditingApi } from "../useBoardEditing.js";
import { useTranslation } from "../../i18n";

export function BoardEditPopover({ editing }: { editing: BoardEditingApi }): ReactElement | null {
  const action = editing.action;
  if (!action) return null;
  return (
    <div className="fixed top-16 left-1/2 -translate-x-1/2 z-scrim">
      <div className="rounded-md border border-slate-700 bg-slate-900/(--tint-solid) shadow-xl p-2 min-w-64">
        <ActionBody action={action} editing={editing} />
      </div>
    </div>
  );
}

function ActionBody({ action, editing }: { action: BoardEditAction; editing: BoardEditingApi }): ReactElement {
  const { t } = useTranslation("boardEdit");
  switch (action.kind) {
    case "rename":
      return (
        <SingleInput
          title={t("renameTitle")}
          initial={action.currentName}
          placeholder={t("renamePlaceholder")}
          onCommit={(v) => editing.commitRename(v)}
          onCancel={editing.cancel}
        />
      );
    case "addModule":
      return (
        <KindNameInputs
          title={action.parentId ? t("addModuleInside") : t("addModule")}
          kindInitial="module"
          namePlaceholder={t("addModuleNamePlaceholder")}
          onCommit={(kind, name) => editing.commitAddModule(kind, name)}
          onCancel={editing.cancel}
        />
      );
    case "addInterface":
      return (
        <KindNameInputs
          title={t("addInterface")}
          kindInitial="rest_read"
          namePlaceholder={t("addInterfaceNamePlaceholder")}
          onCommit={(kind, name) => editing.commitAddInterface(kind, name)}
          onCancel={editing.cancel}
        />
      );
    case "moveModule":
      return (
        <SingleInput
          title={t("moveModuleTitle")}
          initial=""
          placeholder={t("moveModulePlaceholder")}
          options={action.options}
          onCommit={(v) => editing.commitMoveModule(v)}
          onCancel={editing.cancel}
        />
      );
    case "edgeChooser":
      return <EdgeChooser editing={editing} />;
  }
}

function SingleInput(props: {
  title: string;
  initial: string;
  placeholder: string;
  options?: readonly string[];
  onCommit: (value: string) => void;
  onCancel: () => void;
}): ReactElement {
  const [value, setValue] = useState(props.initial);
  const ref = useRef<HTMLInputElement>(null);
  const listId = useRef(props.options ? `bep-${Math.random().toString(36).slice(2, 9)}` : undefined);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-slate-400">{props.title}</div>
      <input
        ref={ref}
        aria-label={props.title}
        className="w-full bg-slate-800 border border-slate-700 rounded-sm px-2 py-1 text-sm text-slate-200 focus:border-slate-500 focus:outline-hidden"
        value={value}
        placeholder={props.placeholder}
        list={listId.current}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") props.onCommit(value.trim());
          if (e.key === "Escape") props.onCancel();
        }}
        onBlur={() => props.onCancel()}
      />
      {props.options && listId.current && (
        <datalist id={listId.current}>
          {props.options.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      )}
    </div>
  );
}

function KindNameInputs(props: {
  title: string;
  kindInitial: string;
  namePlaceholder: string;
  onCommit: (kind: string, name: string) => void;
  onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation("boardEdit");
  const [kind, setKind] = useState(props.kindInitial);
  const [name, setName] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => nameRef.current?.focus(), []);
  const commit = (): void => props.onCommit(kind.trim(), name.trim());
  const keys = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") props.onCancel();
  };
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-slate-400">{props.title}</div>
      <div className="flex gap-1.5">
        <input
          aria-label={t("kindAria", { title: props.title })}
          className="w-28 bg-slate-800 border border-slate-700 rounded-sm px-2 py-1 text-sm font-mono text-slate-300 focus:border-slate-500 focus:outline-hidden"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          onKeyDown={keys}
        />
        <input
          ref={nameRef}
          aria-label={t("nameAria", { title: props.title })}
          className="flex-1 bg-slate-800 border border-slate-700 rounded-sm px-2 py-1 text-sm text-slate-200 focus:border-slate-500 focus:outline-hidden"
          value={name}
          placeholder={props.namePlaceholder}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={keys}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="xs" onClick={props.onCancel}>
          {t("cancel")}
        </Button>
        <Button variant="primary" size="xs" onClick={commit}>
          {t("add")}
        </Button>
      </div>
    </div>
  );
}

function EdgeChooser({ editing }: { editing: BoardEditingApi }): ReactElement {
  const { t } = useTranslation("boardEdit");
  const [name, setName] = useState("");
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-slate-400">{t("edgeChooserTitle")}</div>
      <div className="max-h-48 overflow-y-auto space-y-0.5">
        {editing.processChoices.map((p) => (
          <button
            key={p.id}
            className="w-full text-left text-sm px-2 py-1 rounded-sm text-slate-300 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            onClick={() => editing.commitEdge(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="border-t border-slate-800 pt-1.5 flex gap-1.5 items-center">
        <Tooltip content={t("newProcessTitle")}>
          <Button
            variant="ghost"
            size="sm"
            className="whitespace-nowrap"
            onClick={() => editing.commitEdge("new", name)}
          >
            {t("newProcess")}
          </Button>
        </Tooltip>
        <input
          aria-label={t("newProcessNameAria")}
          className="flex-1 bg-slate-800 border border-slate-700 rounded-sm px-2 py-1 text-xs text-slate-300 placeholder:text-slate-600 focus:border-slate-500 focus:outline-hidden"
          placeholder={t("newProcessNamePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") editing.commitEdge("new", name);
            if (e.key === "Escape") editing.cancel();
          }}
        />
      </div>
    </div>
  );
}
